import {
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type PriceCalculationUnitValueType,
    type BoundBooleanValue,
    type ParameterValueKind,
    type ServiceDefinition,
    PriceCalculationParameterInfo,
} from "../api";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a boolean value with custom labels for true and false states.
 */
export class ResolvedBooleanValue extends ResolvedParameterValue<boolean, BoundBooleanValue> {
    readonly trueLabel: string;
    readonly falseLabel: string;
    readonly trueValue?: PriceCalculationUnitValueType;
    readonly falseValue?: PriceCalculationUnitValueType;
    readonly unit?: PriceCalculationUnit;
    /**
     *
     * @param value The labels for true and false states bound to a parameter
     */
    constructor(value: Omit<BoundBooleanValue, "kind">) {
        super({
            ...value,
            kind: "boolean",
        });
        const { trueLabel, falseLabel, trueValue, falseValue, unit } = value;
        this.trueLabel = trueLabel;
        this.falseLabel = falseLabel;
        this.trueValue = trueValue;
        this.falseValue = falseValue;
        this.unit = unit;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the label for the true state.
     *
     * @returns The true state label
     */
    getTrueLabel(): string {
        return this.trueLabel;
    }

    /**
     * Gets the label for the false state.
     *
     * @returns The false state label
     */
    getFalseLabel(): string {
        return this.falseLabel;
    }

    /**
     * Gets the unit for the calculation parameter.
     *
     * @returns The unit for the calculation parameter
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        if (this.unit === undefined) {
            return [];
        }
        return [
            {
                id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                parameterId: this.parameterId,
                unit: this.unit,
                combineWithParameters: this.combineWithParameters,
            },
        ];
    }

    /**
     * Gets the value for the calculation parameter.
     *
     * @param value The value of the parameter
     * @returns The value for the calculation parameter
     */
    getCalculationParameterValues(value: boolean | undefined): Record<string, PriceCalculationValueWithUnit> {
        if (this.unit === undefined) {
            return {};
        }
        if (value === true && this.trueValue !== undefined) {
            return { [this.parameterId.toString()]: { value: this.trueValue, unit: this.unit } };
        }
        if ((value === false || value === undefined) && this.falseValue !== undefined) {
            return { [this.parameterId.toString()]: { value: this.falseValue, unit: this.unit } };
        }
        return {};
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param values The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(value: boolean | undefined): string | undefined {
        if (value === undefined) {
            return undefined;
        }
        return value ? this.trueLabel : this.falseLabel;
    }

    /**
     * Creates a customer-safe version of this value.
     * This value type has no pricing data to strip.
     *
     * @param _requestPricing - The pricing mode (unused - no pricing data in this type)
     * @returns This instance (no changes needed for any pricing mode)
     */
    toCustomerValue(_requestPricing: ServiceDefinition.RequestPricing): this {
        return this;
    }

    /**
     * Creates a new boolean value with custom labels.
     *
     * @param value The labels for true and false states bound to a parameter
     * @returns A new boolean value or throws an error if the labels are empty
     */
    static create(value: Omit<BoundBooleanValue, "kind">): ResolvedBooleanValue | never {
        const { trueLabel, falseLabel } = value;
        if (!trueLabel || trueLabel.trim() === "") {
            throw new Error("BooleanValue: 'trueLabel' cannot be empty.");
        }

        if (!falseLabel || falseLabel.trim() === "") {
            throw new Error("BooleanValue: 'falseLabel' cannot be empty.");
        }

        return new ResolvedBooleanValue(value);
    }
}
